import type { PostgrestError } from '@supabase/supabase-js';
import { sanitizeFailureDiagnostics } from './failureDiagnostics';
import type { AdminHomeSection } from '@/lib/services/adminHomePulse';

const REDACT_PATTERNS: RegExp[] = [
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi,
  /eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g,
  /Bearer\s+\S+/gi,
  /\bapikey\s*[:=]\s*\S+/gi,
  /\bpassword\s*[:=]\s*\S+/gi,
  /\bsecret\s*[:=]\s*\S+/gi,
  /\btoken\s*[:=]\s*\S+/gi,
];

const CONTEXT_ALLOWLIST = new Set([
  'action',
  'buildId',
  'recoveryReason',
  'scriptAttribution',
  'authAttemptId',
  'authProvider',
  'authSelectBy',
  'authStage',
  'partnerSlug',
  'tagSlug',
  'gameMode',
  'gameRunId',
  'quizId',
  'questionId',
  'answerSubmissionId',
  'answerId',
  'answerPersistenceAttempt',
  'answerPersistenceOutcome',
  'answerPersistencePhase',
  'generationJobId',
  'incidentRef',
  'issueTitle',
  'adminHomeSection',
  'isEmbed',
  'theme',
  'view',
]);

const ADMIN_HOME_SECTION_ALLOWLIST: Readonly<Record<AdminHomeSection, true>> = {
  retention: true,
  players: true,
  operations: true,
};

export function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}…`;
}

export function redactString(value: string): string {
  return REDACT_PATTERNS.reduce((acc, pattern) => acc.replace(pattern, '[REDACTED]'), value);
}

export function sanitizeUrl(url: string): { host?: string; path?: string } {
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    return {
      host: parsed.host,
      path: parsed.pathname,
    };
  } catch {
    return { path: redactString(url.split('?')[0] ?? url) };
  }
}

export function sanitizeContext(
  context: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!context) return {};

  const sanitized: Record<string, unknown> = sanitizeFailureDiagnostics(context);
  for (const [key, value] of Object.entries(context)) {
    if (!CONTEXT_ALLOWLIST.has(key)) continue;
    if (
      key === 'gameRunId' &&
      (typeof value !== 'string' ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value))
    ) {
      continue;
    }
    if (
      key === 'adminHomeSection' &&
      (typeof value !== 'string' || !Object.hasOwn(ADMIN_HOME_SECTION_ALLOWLIST, value))
    ) {
      continue;
    }
    if (typeof value === 'string') {
      sanitized[key] = redactString(truncate(value, 500));
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

export function sanitizeBreadcrumbData(data: Record<string, unknown>): Record<string, unknown> {
  return sanitizeContext(data);
}

export function sanitizeSupabaseError(
  error: PostgrestError | null | undefined
): { code?: string; hint?: string; details?: string } | undefined {
  if (!error) return undefined;
  return {
    code: error.code,
    hint: error.hint ? redactString(truncate(error.hint, 500)) : undefined,
    details: error.details ? redactString(truncate(String(error.details), 500)) : undefined,
  };
}

export function normalizeError(error: unknown): { name?: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: redactString(truncate(error.message || 'Unknown error', 2000)),
      stack: error.stack ? redactString(truncate(error.stack, 8000)) : undefined,
    };
  }

  if (typeof error === 'string') {
    return { message: redactString(truncate(error, 2000)) };
  }

  try {
    return {
      message: redactString(truncate(JSON.stringify(error), 2000)),
    };
  } catch {
    return { message: 'Unknown error' };
  }
}

export function sanitizeNetwork(network: {
  method?: string;
  url?: string;
  status?: number;
}): { method?: string; host?: string; path?: string; status?: number } | undefined {
  if (!network.url && network.status === undefined && !network.method) {
    return undefined;
  }

  const urlParts = network.url ? sanitizeUrl(network.url) : {};
  return {
    method: network.method,
    host: urlParts.host,
    path: urlParts.path,
    status: network.status,
  };
}
